import { useCallback, useEffect, useRef, useState } from 'react'
import { Platform, View, Text } from 'react-native'
import { Stack, useRouter, useGlobalSearchParams, usePathname } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { X, WifiOff } from '@/components/ui/icons'
import { EmptyState } from '@/components/ui/EmptyState'
import { BackButton } from '@/components/ui/BackButton'
import { Pressable } from '@/components/ui/Pressable'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { Skeleton } from '@/components/ui/Skeleton'
import { IndividualHostingComingSoon } from '@/components/hosting/IndividualHostingComingSoon'
import { EditDiscardsDraftDialog } from '@/components/hosting/EditDiscardsDraftDialog'
import {
  useListingWizardStore,
  hasListingDraft,
  WIZARD_STEPS,
} from '@/lib/stores/listing-wizard.store'
import { vehicleService } from '@/lib/services/vehicle.service'
import { useHostingEligibility } from '@/lib/hooks/use-hosting-eligibility'
import { useSemanticColors, SPACING, RADIUS, TEXT_STYLES, INTERACTION } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { platformStackAnimation } from '@/lib/navigation/stable-stack-options'
import { captureError } from '@/lib/utils/observability'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'

const STEP_I18N_KEYS: Record<string, string> = {
  details: 'listing.step_details',
  ownership: 'listing.step_ownership',
  features: 'listing.step_features',
  photos: 'listing.step_photos',
  location: 'listing.step_location',
  pricing: 'listing.step_pricing',
  documents: 'listing.step_documents',
  review: 'listing.step_review',
}

/**
 * Returns `null` for routes that are not wizard steps (e.g. the `insurance`
 * redirect). Falling back to `0` made the progress bar and the "step N of 7"
 * caption snap back to step 1 every time one of those routes was mounted.
 */
function wizardStepIndexFromPath(pathname: string): number | null {
  const step = pathname.split('/').filter(Boolean).at(-1)
  const index = WIZARD_STEPS.indexOf(step as (typeof WIZARD_STEPS)[number])

  return index >= 0 ? index : null
}

function wizardHrefForStep(step: number, vehicleId: string | null): string {
  const stepName = WIZARD_STEPS[Math.max(0, Math.min(step, WIZARD_STEPS.length - 1))] ?? 'details'
  const base = `/hosting/new-listing/${stepName}`

  return vehicleId ? `${base}?vehicleId=${encodeURIComponent(vehicleId)}` : base
}

function WizardHeader() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const { vehicleId: searchVehicleId } = useGlobalSearchParams<{ vehicleId?: string }>()
  const { currentStep, prevStep, entryMode, editReturnTo, dirtySinceLoad, vehicleId } =
    useListingWizardStore()
  const discardConfirm = useConfirmDialog()

  // Is there anything to lose? An untouched wizard should close on one tap;
  // only a draft with real work in it earns a question.
  // One shared definition. This copy ignored `documents`, so a host whose only
  // work was the tarjeta scan got no question at all — and the sweep that
  // followed had a wider blast radius than the state it was clearing.
  const hasDraft = useListingWizardStore(
    (s) =>
      hasListingDraft(s) ||
      Object.keys(s.documents ?? {}).length > 0 ||
      Object.keys(s.location ?? {}).length > 0,
  )

  // "Editar" is a property of how the wizard was OPENED, never of whether a
  // row exists yet. A publish that fails halfway leaves `vehicleId` set on the
  // store, which used to relabel the whole wizard mid-flow.
  const isEditMode = !!searchVehicleId || entryMode === 'edit'

  const totalSteps = WIZARD_STEPS.length
  const stepName = WIZARD_STEPS[currentStep] ?? 'details'
  // Completed-and-current steps over total — step 1 of 7 is 1/7 of the way in,
  // not 0%, and Revisar only reads 100% once it is the last thing left. Matches
  // the "Paso {current} de {total}" caption right above it.
  const progress = totalSteps > 0 ? (currentStep + 1) / totalSteps : 0

  // Back is always available. On step 0 it means "leave the wizard", not
  // "router.back()" — that path used to land on an empty nested-stack frame
  // under `/hosting/new-listing` (no index route) and paint a blank screen.
  const canGoBack = true

  // replace, never back: explicit exit from the wizard stack so we never
  // surface a blank parent frame if history is messy (web + nested stacks).
  const leaveWizard = () => router.replace('/hosting')

  /**
   * SAVE AND EXIT — the safe way out, and now the default one.
   *
   * The draft is persisted on every `set`, so leaving costs the host nothing.
   * Until now the only labelled exit was a dialog that said they would lose
   * everything, which is both false and the reason a host would rather abandon
   * the app than risk the button.
   */
  const saveAndLeave = () => leaveWizard()

  // The X used to call reset() straight through: one tap, and every photo,
  // both document scans and everything typed were gone with nothing in
  // between and no undo.
  const handleClose = () => {
    if (isEditMode && !dirtySinceLoad) {
      leaveWizard()
      return
    }
    if (hasDraft) {
      discardConfirm.open()
      return
    }
    // Nothing to lose and nothing confirmed: clear state, keep files.
    useListingWizardStore.getState().reset()
    leaveWizard()
  }

  const handleBack = () => {
    if (isEditMode) {
      if (stepName === 'review') handleClose()
      else {
        useListingWizardStore.getState().setEditReturnTo(null)
        router.dismissTo(wizardHrefForStep(WIZARD_STEPS.indexOf('review'), vehicleId) as never)
      }
      return
    }
    // First step: leave the wizard cleanly. Same as close — confirm only when
    // there is something to lose. Never router.back() from here.
    // Step 0 normally means "leave the wizard" — but not when the host got here
    // from Revisar's Editar: that step was PUSHED on top of Revisar, so the frame
    // to return to is already on the stack. Without this they met the exit dialog
    // and the only way back to Revisar was walking all seven steps again.
    if (currentStep === 0 && !editReturnTo) {
      handleClose()
      return
    }
    // Every step's Next is a `push`, so the entry we want is already sitting on
    // the stack: pop it instead of stacking a duplicate over it, or the history
    // grows on every correction. `currentStep` then re-syncs from the pathname
    // (the effect in NewListingLayout) — the only thing that knows where `back`
    // actually landed, which is not always `currentStep - 1` once Revisar's
    // per-section Edit jumps are in play.
    //
    // Guard: only pop if the previous route is still inside the wizard. A bare
    // back from step 1 could otherwise exit to a blank parent when the stack
    // was deep-linked or history was rewritten mid-edit.
    if (router.canGoBack()) {
      router.back()
      return
    }
    const previousStep = currentStep - 1
    prevStep()
    // Deep-linked straight into a later step — nothing to pop. Only an id that
    // came in through the URL means "opened for editing"; the store's id is
    // also set mid-publish, and putting that one in the URL made the layout's
    // edit loader re-hydrate from the half-saved server row and destroy the
    // local draft (photos + both document scans) on a single Back tap.
    router.replace(wizardHrefForStep(previousStep, searchVehicleId ?? null) as never)
  }

  return (
    <View
      style={{
        // Fixed footprint prevents React Navigation from retaining a taller
        // measurement from another wizard step and exposing a blank band.
        height: Platform.OS === 'ios' ? 164 : 140,
        paddingTop: insets.top + SPACING.sm,
        paddingBottom: SPACING.sm,
        paddingHorizontal: SPACING.lg,
        backgroundColor: colors.background,
      }}
    >
      {/* Top row: back / title / close */}
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: SPACING.sm }}>
        {canGoBack ? (
          <BackButton onPress={handleBack} testID="listing.back-button" />
        ) : (
          <View style={{ width: 44, height: 44 }} />
        )}

        <View style={{ flex: 1, minWidth: 0, alignItems: 'center', paddingHorizontal: SPACING.xs }}>
          <Text
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.82}
            style={{
              ...TEXT_STYLES.h3,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
          >
            {isEditMode
              ? t('hosting.edit_vehicle')
              : t(STEP_I18N_KEYS[stepName] ?? 'listing.step_details')}
          </Text>
          <Text
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.82}
            style={{
              fontSize: 12,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              marginTop: 2,
            }}
          >
            {/* The step name only belongs here when "Editar" has taken over the
                title — otherwise it printed the same words twice, stacked. */}
            {isEditMode
              ? t(
                  stepName === 'review'
                    ? 'hosting.edit_overview'
                    : (STEP_I18N_KEYS[stepName] ?? 'listing.step_details'),
                )
              : t('listing.step_of', { current: currentStep + 1, total: totalSteps })}
          </Text>
        </View>

        <Pressable
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
          testID="listing.close-button"
          style={({ pressed }) => ({
            width: 44,
            height: 44,
            borderRadius: RADIUS.full,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.92)',
            borderWidth: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(26,21,37,0.08)',
            opacity: pressed ? 0.82 : 1,
            ...WEB_SHADOWS.sm,
          })}
        >
          <X size={20} color={colors.textPrimary} strokeWidth={2.2} />
        </Pressable>
      </View>

      {/* Progress bar */}
      {!isEditMode ? (
        <ProgressBar
          value={progress}
          height={4}
          fillColor={COLORS.coral.DEFAULT}
          trackColor={isDark ? colors.border : colors.surfaceSubtle}
          borderRadius={RADIUS.full}
        />
      ) : (
        <View style={{ height: 4 }} />
      )}

      {/* Say it WHILE they work, not at the moment we ask them to throw it away.
          A host part-way through 17 photos has no way to know the draft survives,
          so every interruption feels like a risk. */}
      {/* Keep the custom navigation header's measured height invariant. React
          Navigation does not reliably re-measure a custom header that becomes
          shorter after changing steps; conditional requirement banners here
          left their old height behind as a blank block over every later screen.
          Requirements now live in step 1's scrollable content. */}
      <View style={{ minHeight: 16, marginTop: SPACING.xs }}>
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.82}
          accessible={hasDraft}
          accessibilityElementsHidden={!hasDraft}
          importantForAccessibility={hasDraft ? 'auto' : 'no-hide-descendants'}
          style={{
            fontSize: 12,
            lineHeight: 16,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            opacity: hasDraft ? 1 : 0,
          }}
          testID={hasDraft ? 'listing.draft-saved-hint' : undefined}
        >
          {t(isEditMode ? 'hosting.edit_hint' : 'hosting.draft_autosaved_hint')}
        </Text>
      </View>

      {/* BOTH labelled actions are safe, on purpose.
          The cancel slot used to hold "Descartar", wired to delete every
          capture — with destructive={false}, so no red and no warning, while
          the message reassured the host that we save their progress. In every
          system convention that left/outline button means "no, take me back":
          a host tapping it to STAY lost 17 live photos and both document scans.
          Primary = save and exit; cancel = stay. The deliberate discard already
          exists, correctly labelled and correctly warned, as "Empezar de cero"
          in ResumeDraftDialog — there is no reason for a second one here. */}
      <ConfirmDialog
        visible={discardConfirm.visible}
        title={t(isEditMode ? 'hosting.edit_exit_title' : 'hosting.exit_draft_title')}
        message={t(isEditMode ? 'hosting.edit_exit_body' : 'hosting.exit_draft_body')}
        confirmLabel={t(isEditMode ? 'hosting.edit_exit_later' : 'hosting.exit_draft_save')}
        cancelLabel={t('hosting.exit_draft_stay')}
        destructive={false}
        onConfirm={() => {
          discardConfirm.close()
          saveAndLeave()
        }}
        onCancel={discardConfirm.close}
        onDismiss={discardConfirm.close}
        testIDs={{
          overlay: 'listing.exit-draft-dialog',
          confirm: 'listing.exit-draft-save',
          cancel: 'listing.exit-draft-stay',
        }}
      />
    </View>
  )
}

export default function NewListingLayout() {
  const { t } = useTranslation()
  const router = useRouter()
  const { canHost, isLoading } = useHostingEligibility()
  const pathname = usePathname()
  // A nested Stack layout's local params belong to its parent route, not the
  // active child screen. Reading them here lost ?vehicleId on direct links.
  // Scope global params to this wizard so a background layout never loads a
  // vehicle from an unrelated booking/detail route.
  const { vehicleId: routeVehicleId } = useGlobalSearchParams<{ vehicleId?: string }>()
  const searchVehicleId =
    pathname.startsWith('/hosting/new-listing/') && typeof routeVehicleId === 'string'
      ? routeVehicleId
      : undefined
  const currentStep = useListingWizardStore((s) => s.currentStep)
  const goToStep = useListingWizardStore((s) => s.goToStep)
  const storeVehicleId = useListingWizardStore((s) => s.vehicleId)
  const loadFromVehicle = useListingWizardStore((s) => s.loadFromVehicle)
  const loadedVehicleRef = useRef<string | null>(null)
  const [isLoadingEditVehicle, setIsLoadingEditVehicle] = useState(false)
  // An edit deep link that would clobber an in-progress create draft, held until
  // the host decides. Covers the doors with no calling screen (explore detail,
  // notification deep links).
  const [pendingEditCollision, setPendingEditCollision] = useState<string | null>(null)
  // The vehicle whose fetch failed, so the recovery screen can retry that exact
  // id. Null whenever the wizard is usable.
  const [editLoadFailedId, setEditLoadFailedId] = useState<string | null>(null)

  const pathStep = wizardStepIndexFromPath(pathname)
  const isEditingExistingVehicle = Boolean(searchVehicleId || storeVehicleId)

  /**
   * Fetch the vehicle being edited and hydrate the wizard from it.
   *
   * This lived INSIDE the effect below, and the collision dialog's "descartar y
   * editar" tried to re-trigger it by setting `loadedVehicleRef.current = null`.
   * Mutating a ref renders nothing and re-runs no effect, and neither dependency
   * (`searchVehicleId`, `loadFromVehicle`) changes when the host confirms — so
   * the fetch NEVER happened. The captures had already been deleted by then, and
   * the host was left with an empty wizard bound to a live listing: their work
   * gone, and the edit they asked for impossible.
   *
   * Callable, so the handler can do explicitly what the effect cannot do for it.
   */
  const loadVehicleForEdit = useCallback(
    async (editId: string) => {
      loadedVehicleRef.current = editId
      setEditLoadFailedId(null)
      setIsLoadingEditVehicle(true)
      try {
        const vehicle = await vehicleService.getVehicleById(editId)
        loadFromVehicle(vehicle)
      } catch (err) {
        // A failed fetch used to be swallowed: ref back to null, a __DEV__ log,
        // and the host dropped into an EMPTY wizard still bound to a live
        // vehicleId — no message, no retry. Filling one field and saving would
        // then run updateVehicle over the published row with a blank draft.
        // Remember WHICH id failed, so the recovery screen below can offer to
        // try that exact vehicle again instead of guessing.
        loadedVehicleRef.current = null
        setEditLoadFailedId(editId)
        captureError(err, { screen: 'listing_wizard', action: 'load_vehicle_for_edit' })
      } finally {
        setIsLoadingEditVehicle(false)
      }
    },
    [loadFromVehicle],
  )

  // NO mount-time reset here, deliberately.
  //
  // A guard used to live here that reset an edit-mode draft whenever the layout
  // mounted without a vehicleId param — meant to stop a stale edit draft being
  // inherited by a create entry. It DESTROYED the thing it was protecting:
  // useLocalSearchParams in this LAYOUT does not reliably carry the child route's
  // param on the first render, so a host reopening a saved draft to keep editing
  // it hit the guard, the store was wiped, and they landed on an empty step 1 of 8.
  //
  // The protection now lives where it belongs and cannot misfire: the create
  // doors compute hasDraft as (entryMode === 'create' && hasListingDraft), so a
  // stale edit draft is never resumable there and startFresh() clears it.

  useEffect(() => {
    if (pathStep !== null && currentStep !== pathStep) {
      goToStep(pathStep)
    }
  }, [currentStep, goToStep, pathStep])

  useEffect(() => {
    const editId = searchVehicleId

    if (!editId || loadedVehicleRef.current === editId) {
      return
    }

    // Never re-hydrate on top of a live draft that already belongs to this id.
    // Opening the wizard to edit always arrives with a freshly reset store
    // (`reset()` then `setVehicleId()`), so a draft that is BOTH attached to
    // `editId` and full of unsaved work can only be the half-saved row
    // review.tsx created mid-publish. Loading the server copy back over it
    // wipes the local photos and both document scans.
    const draft = useListingWizardStore.getState()
    const hasUnsavedWork =
      Object.keys(draft.details).length > 0 ||
      Object.keys(draft.features).length > 0 ||
      (draft.photos.photos?.length ?? 0) > 0 ||
      Object.keys(draft.location).length > 0 ||
      Object.keys(draft.pricing).length > 0 ||
      Object.keys(draft.documents).length > 0

    // Skip the refetch ONLY for the case this guard was written for: the
    // half-saved row review.tsx created mid-publish, in this same session.
    //
    // Its premise — "an edit always arrives with a freshly reset store" — stopped
    // being true twice over: notification deep links push the URL with no reset,
    // and persistence keeps a previous session's draft alive, so hasUnsavedWork
    // was unconditionally true after any earlier edit. Skipping then guarantees
    // documentReview stays null (loadFromVehicle is its only writer, and it is
    // excluded from persistence), so the document_review_required panel never
    // renders — the host is sent to fix a rejection and lands on a screen that
    // does not mention it, over stale data they would then overwrite.
    const isSameSessionHalfSave = draft.entryMode === 'create' && draft.vehicleId === editId
    if (isSameSessionHalfSave && hasUnsavedWork) {
      loadedVehicleRef.current = editId
      return
    }

    // Same guard as above, for the OTHER way back into an edit: the doors that
    // push ?vehicleId= with no calling screen (explore detail, notification deep
    // links) never run beginEdit, so the reset does not happen — the overwrite
    // does, right here, when loadFromVehicle drops the server copy on top of
    // changes the host has not saved yet.
    //
    // Resuming costs one thing worth naming: documentReview is not persisted and
    // loadFromVehicle is its only writer, so a rejection panel will not render
    // until the next load. Losing typed work is the worse of the two, and the
    // panel comes back on the next entry.
    if (draft.entryMode === 'edit' && draft.vehicleId === editId && draft.dirtySinceLoad) {
      loadedVehicleRef.current = editId
      return
    }

    // CHOKE POINT for every edit door. The gated ones live in hosting/, but
    // explore's vehicle detail and server-generated notification deep links open
    // ?vehicleId= with no calling screen to ask first — and hydrating here
    // overwrites an in-progress CREATE draft, silently, with 17 live captures in
    // it. This is the only place all of them pass through.
    //
    // Ask instead of destroying. Until the host answers, do not fetch and do not
    // touch the store.
    if (draft.entryMode === 'create' && draft.vehicleId !== editId && hasUnsavedWork) {
      setPendingEditCollision(editId)
      return
    }

    void loadVehicleForEdit(editId)
  }, [searchVehicleId, loadVehicleForEdit])

  // A skeleton shaped like the form it replaces — header block, progress bar,
  // then the field rows the wizard is about to render. A single centred line of
  // text told the host nothing about what was coming and made the arrival jump.
  const waitingForEdit =
    !!searchVehicleId &&
    loadedVehicleRef.current !== searchVehicleId &&
    !editLoadFailedId &&
    !pendingEditCollision
  if (isLoading || isLoadingEditVehicle || waitingForEdit) {
    return (
      <ScreenContainer variant="content" scroll={false}>
        <View
          accessibilityRole="progressbar"
          accessibilityLabel={t('hosting.org_loading')}
          testID="listing.wizard-loading"
          style={{ padding: SPACING.md, gap: SPACING.md }}
        >
          <Skeleton width="55%" height={26} borderRadius={RADIUS.sm} />
          <Skeleton width="100%" height={4} borderRadius={RADIUS.full} />
          {[0, 1, 2, 3].map((i) => (
            <View key={i} style={{ gap: SPACING.xs }}>
              <Skeleton width="35%" height={14} borderRadius={RADIUS.xs} staggerIndex={i} />
              <Skeleton
                width="100%"
                height={INTERACTION.fieldHeight}
                borderRadius={RADIUS.md}
                staggerIndex={i}
              />
            </View>
          ))}
        </View>
      </ScreenContainer>
    )
  }

  // A failed edit fetch is a DEAD END otherwise. The host tapped their own car
  // and got an empty 8-step form still bound to its id: nothing said the load
  // failed, nothing offered a retry, and filling in one field and saving would
  // have run updateVehicle over the published row with a blank draft. Recovery
  // first, and say plainly that the listing itself was not touched.
  if (editLoadFailedId) {
    return (
      <ScreenContainer variant="content" scroll={false}>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <EmptyState
            variant="bloom"
            illustration={<WifiOff size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />}
            title={t('listing.edit_load_failed_title')}
            description={t('listing.edit_load_failed_body')}
            action={{
              label: t('common.retry'),
              onPress: () => void loadVehicleForEdit(editLoadFailedId),
            }}
            secondaryAction={{
              label: t('hosting.my_listings'),
              onPress: () => router.replace('/(tabs)/hosting' as never),
            }}
          />
        </View>
      </ScreenContainer>
    )
  }

  if (!isLoading && !canHost && !isEditingExistingVehicle) {
    return <IndividualHostingComingSoon />
  }

  return (
    <>
      <EditDiscardsDraftDialog
        visible={pendingEditCollision !== null}
        onKeepDraft={() => {
          const draft = useListingWizardStore.getState()
          setPendingEditCollision(null)
          // Leave the ?vehicleId= route: review.tsx derives isEditMode from the
          // STORE while the header derives it from the URL, so sitting here
          // un-hydrated would title the wizard Editar and then run createVehicle.
          router.replace(wizardHrefForStep(draft.currentStep, draft.vehicleId) as never)
        }}
        onDiscardAndEdit={() => {
          const editId = pendingEditCollision
          setPendingEditCollision(null)
          if (!editId) return
          // discardDraft, never reset: free the captures instead of orphaning them.
          useListingWizardStore.getState().discardDraft()
          useListingWizardStore.getState().beginEdit(editId)
          // Fetch explicitly. The effect will NOT do it for us — its deps are
          // unchanged and a ref mutation re-runs nothing — so this call is the
          // only thing standing between the host and an empty wizard pointed at
          // the live listing they just asked to edit.
          void loadVehicleForEdit(editId)
        }}
        onDismiss={() => {
          const draft = useListingWizardStore.getState()
          setPendingEditCollision(null)
          router.replace(wizardHrefForStep(draft.currentStep, draft.vehicleId) as never)
        }}
        testIDPrefix="wizard"
      />
      <Stack
        screenOptions={{
          headerShown: true,
          header: () => <WizardHeader />,
          // Each next step slides in from the right to
          // reinforce forward progression. The header's progress bar
          // (line 169 above) advances synchronously. Web stays instant
          // to avoid SPA hydration flicker between steps.
          animation: platformStackAnimation('slide_from_right'),
        }}
      />
    </>
  )
}
